import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo, faEdit, faTrash, faXmark } from '@fortawesome/free-solid-svg-icons';
import defaultFoodImage from './assets/DefaultFood.jpg';
import {
    fetchRestaurantFoodItems,
    loadGroupedOnlineMenuItems,
    formatOnlineIngredientsDisplay,
    formatIngredientsTablePreview,
    formatFoodItemDisplayName,
    parseIngredientsDisplayList,
    getCatalogOptionsForTemplateSource,
    foodItemMatchesTemplateSourceCatalog,
    isMenuItemActiveStatus,
} from './onlineFoodItems';
import {
    addDailyMenuFromTemplate,
    fetchMenuTemplates,
    getLocalDateString,
    pickDefaultMenuTemplateForDate,
    recordDailyMenuTemplateDate,
    formatDailyMenuAvailability,
    resolveDailyMenuRowId,
    weekdayNameFromDateString,
} from './dailyMenuApi';
import { fetchMenuTemplateById } from './menuTemplateItemUtils';
import MealTypesDropdown, { parseMealsCsv } from './mealTypesDropdown';
import SearchableSelect from './SearchableSelect';
import JreatsStatewideMenuBrowser from './JreatsStatewideMenuBrowser';
import OrdersSortableTh from './OrdersSortableTh';
import OrdersTablePanel from './OrdersTablePanel';
import OrdersTablePagination from './OrdersTablePagination';
import { buildSortHandlers, sortList, str } from './tableSortUtils';
import {
    AgePricingComboCell,
    AgePricingComboEditor,
    hasAgePricing,
} from './AgePricingDisplay';
import './css/AgePricingDisplay.css';
import './css/DailyMenu.css';
import './css/Menu.css';
import './css/Inventory.css';
import './css/OrdersTablePagination.css';

/** Food item ids already on the daily menu for the current date + source. */
function buildDailyMenuExistingFoodIds(dailyMenuItems) {
    const ids = new Set();
    const rows = Array.isArray(dailyMenuItems) ? dailyMenuItems : [];
    for (const row of rows) {
        const id =
            row.Food_Item_Id ??
            row.food_item_id ??
            row.foodItemId ??
            row.Food_item_id;
        if (id != null && String(id).trim() !== '') {
            ids.add(String(id));
        }
    }
    return ids;
}

function menuItemIsInDailyMenu(existingFoodIds, menuItem) {
    const id =
        menuItem?.Food_Item_Id ??
        menuItem?.food_item_id ??
        menuItem?.foodItemId;
    return id != null && existingFoodIds.has(String(id));
}

function defaultDailyMenuPickerDraft(menuItem) {
    const tiers = Array.isArray(menuItem?.agePricing) ? menuItem.agePricing : [];
    const tierPrice = tiers[0]?.price ?? tiers[0]?.Price;
    return {
        meal: String(menuItem?.Meal ?? menuItem?.meal ?? '').trim(),
        noOfItemsAvailable: String(
            menuItem?.No_of_items_available ??
                menuItem?.no_of_items_available ??
                '50'
        ),
        price: String(
            tierPrice ??
                menuItem?.Price ??
                menuItem?.price ??
                ''
        ),
    };
}

function getDailyMenuPickerRowDraft(drafts, foodId, menuItem) {
    return drafts[foodId] ?? defaultDailyMenuPickerDraft(menuItem);
}

function getAddItemCatalogSortValue({ foodType, item }, sortKey) {
    switch (sortKey) {
        case 'foodItem':
            return str(item?.Food_Item);
        case 'category':
            return str(item?.Food_Category ?? item?.foodCategory);
        case 'price':
            return Number.parseFloat(item?.Price) || 0;
        case 'quantity':
            return Number.parseFloat(item?.Quantity) || 0;
        case 'foodType':
            return str(foodType ?? item?.itemFoodType ?? item?.Food_Type);
        case 'availability':
            return Number.parseFloat(item?.No_of_items_available) || 0;
        case 'meal':
            return str(item?.Meal ?? item?.meal);
        default:
            return '';
    }
}

function compareDailyMenuRows(a, b, sortKey, direction, getSortValue) {
    const va = getSortValue(a, sortKey);
    const vb = getSortValue(b, sortKey);
    let cmp = 0;
    if (typeof va === 'number' && typeof vb === 'number') {
        cmp = va - vb;
    } else {
        cmp = String(va).localeCompare(String(vb), undefined, {
            numeric: true,
            sensitivity: 'base',
        });
    }
    return direction === 'asc' ? cmp : -cmp;
}

function resolveDailyMenuQuantity(item) {
    return (
        item?.Quantity ??
        item?.quantity ??
        item?.food_item_quantity ??
        item?.foodItemQuantity ??
        ''
    );
}

const DailyMenu = ({
    dailyMenuItems,
    handleAddDailyMenu,
    editingItemId,
    tempDailyMenuItem,
    setTempDailyMenuItem,
    handleUpdateDailyMenuItem,
    handleCloseEdit,
    handleEditDailyMenuItem,
    handleDeleteDailyMenuItem,
    showMenuTable,
    menuItems,
    addedDailyMenuItems,
    handleBulkAddDailyMenuItems,
    closeAddItemModal,
    restaurantId,
    currentPage = 1,
    itemsPerPage = 10,
    onPageChange,
    menuTypeFilter = 'online',
    viewMenuDate = getLocalDateString(),
    onViewMenuDateChange,
    onRefreshDailyMenu,
    dailyMenuSourceFilter = 'MYEATS',
}) => {
    const [dailyMenuSearch, setDailyMenuSearch] = useState('');
    const [dailyMenuSort, setDailyMenuSort] = useState(null);
    const dailySearchSkipInitialPageReset = useRef(true);
    const [showAddTemplateModal, setShowAddTemplateModal] = useState(false);
    const [menuTemplates, setMenuTemplates] = useState([]);
    const [templatesLoading, setTemplatesLoading] = useState(false);
    const [selectedTemplateId, setSelectedTemplateId] = useState('');
    const [menuDate, setMenuDate] = useState(() => getLocalDateString());
    const [addingFromTemplate, setAddingFromTemplate] = useState(false);
    const [templatePreviewItems, setTemplatePreviewItems] = useState([]);
    const [templatePreviewLoading, setTemplatePreviewLoading] = useState(false);
    const [templatePreviewError, setTemplatePreviewError] = useState('');
    const [pickerMenuItems, setPickerMenuItems] = useState({});
    const [jreatsMenuMeta, setJreatsMenuMeta] = useState(null);
    const [menuSourceLoadError, setMenuSourceLoadError] = useState('');
    const [addItemSearch, setAddItemSearch] = useState('');
    const [addItemPage, setAddItemPage] = useState(1);
    const [addItemPageSize, setAddItemPageSize] = useState(10);
    const [addItemSort, setAddItemSort] = useState(null);
    const [dailyMenuPageSize, setDailyMenuPageSize] = useState(itemsPerPage);
    const [selectedFoodIds, setSelectedFoodIds] = useState(() => new Set());
    const [pickerRowDrafts, setPickerRowDrafts] = useState({});
    const [addingBulkItems, setAddingBulkItems] = useState(false);
    const [ingredientsPopup, setIngredientsPopup] = useState(null);
    const minMenuDate = useMemo(() => getLocalDateString(), [showAddTemplateModal]);
    const todayDate = useMemo(() => getLocalDateString(), []);
    const isPastMenuDate = Boolean(viewMenuDate && String(viewMenuDate).trim() < todayDate);

    const templateSelectOptions = useMemo(
        () =>
            menuTemplates.map((t) => {
                const day = t.dayOfWeek
                    ? ` (${String(t.dayOfWeek).charAt(0).toUpperCase()}${String(t.dayOfWeek).slice(1).toLowerCase()})`
                    : '';
                const defaultTag = t.isDefault ? ' · Default' : '';
                return {
                    value: t.templateId,
                    label: `${t.templateName ?? 'Template'}${day}${defaultTag}`,
                };
            }),
        [menuTemplates]
    );

    const menuDateWeekday = useMemo(() => weekdayNameFromDateString(menuDate), [menuDate]);
    const menuDateWeekdayLabel = useMemo(() => {
        const raw = String(menuDateWeekday || '').toLowerCase();
        if (!raw) return '—';
        return raw.charAt(0).toUpperCase() + raw.slice(1);
    }, [menuDateWeekday]);

    const selectedTemplate = useMemo(
        () =>
            menuTemplates.find((t) => String(t.templateId) === String(selectedTemplateId)) || null,
        [menuTemplates, selectedTemplateId]
    );

    const currentUser = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('user') || 'null');
        } catch {
            return null;
        }
    }, []);

    useEffect(() => {
        if (!showAddTemplateModal || !restaurantId) return;
        let cancelled = false;
        setTemplatesLoading(true);
        fetchMenuTemplates(restaurantId, { templateSource: dailyMenuSourceFilter })
            .then((list) => {
                if (!cancelled) {
                    const active = list.filter(
                        (t) => String(t.status ?? '').trim().toLowerCase() !== 'inactive'
                    );
                    setMenuTemplates(active);
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    console.error('Error loading menu templates:', err?.response?.data || err);
                    setMenuTemplates([]);
                }
            })
            .finally(() => {
                if (!cancelled) setTemplatesLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [showAddTemplateModal, restaurantId, dailyMenuSourceFilter]);

    useEffect(() => {
        if (!showAddTemplateModal) return;
        const defaultTemplate = pickDefaultMenuTemplateForDate(menuTemplates, menuDate);
        if (defaultTemplate) {
            setSelectedTemplateId(String(defaultTemplate.templateId ?? ''));
        } else {
            setSelectedTemplateId('');
        }
    }, [showAddTemplateModal, menuDate, menuTemplates]);

    useEffect(() => {
        if (!showAddTemplateModal || !selectedTemplateId) {
            setTemplatePreviewItems([]);
            setTemplatePreviewError('');
            setTemplatePreviewLoading(false);
            return undefined;
        }
        let cancelled = false;
        setTemplatePreviewLoading(true);
        setTemplatePreviewError('');
        fetchMenuTemplateById(selectedTemplateId)
            .then((data) => {
                if (cancelled) return;
                const rows = Array.isArray(data?.items) ? data.items : [];
                setTemplatePreviewItems(
                    rows.filter((row) => {
                        const selected = row?.isSelected ?? row?.Is_Selected ?? true;
                        return !(selected === false || selected === 0 || selected === '0');
                    })
                );
            })
            .catch((err) => {
                if (cancelled) return;
                console.error('Template preview load failed:', err?.response?.data || err);
                setTemplatePreviewItems([]);
                setTemplatePreviewError(
                    err?.response?.data?.message ||
                        err?.response?.data?.error ||
                        'Could not load template items.'
                );
            })
            .finally(() => {
                if (!cancelled) setTemplatePreviewLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [showAddTemplateModal, selectedTemplateId]);

    const loadPickerMenuItems = useCallback(async () => {
        if (!restaurantId) {
            setPickerMenuItems({});
            setJreatsMenuMeta(null);
            return;
        }
        setMenuSourceLoadError('');
        setJreatsMenuMeta(null);
        try {
            if (dailyMenuSourceFilter === 'JREATS') {
                const { items, meta } = await loadGroupedOnlineMenuItems(restaurantId, 'JREATS');
                setJreatsMenuMeta(meta);
                setPickerMenuItems(items);
                return;
            }
            const grouped = await fetchRestaurantFoodItems(
                restaurantId,
                getCatalogOptionsForTemplateSource(dailyMenuSourceFilter)
            );
            setPickerMenuItems(grouped);
        } catch (err) {
            console.error('Error loading daily menu picker items:', err?.response?.data || err);
            const msg =
                err?.response?.data?.error ||
                err?.response?.data?.message ||
                err?.message ||
                'Failed to load menu items.';
            setMenuSourceLoadError(msg);
            setPickerMenuItems({});
            setJreatsMenuMeta(null);
        }
    }, [restaurantId, dailyMenuSourceFilter]);

    useEffect(() => {
        loadPickerMenuItems();
    }, [loadPickerMenuItems]);

    const dismissAddItemModal = () => {
        if (addingBulkItems) return;
        setAddItemSearch('');
        setSelectedFoodIds(new Set());
        setPickerRowDrafts({});
        if (typeof closeAddItemModal === 'function') {
            closeAddItemModal();
        }
    };

    const addItemSearchQuery = addItemSearch.trim().toLowerCase();

    useEffect(() => {
        setAddItemPage(1);
    }, [addItemSearchQuery]);

    const visibleDailyMenuItems = useMemo(
        () => (Array.isArray(dailyMenuItems) ? dailyMenuItems : []),
        [dailyMenuItems]
    );

    const catalogMenuItems = pickerMenuItems;

    const openAddTemplateModal = () => {
        const preferred = String(viewMenuDate || '').trim();
        const today = getLocalDateString();
        setMenuDate(preferred && preferred >= today ? preferred : today);
        setSelectedTemplateId('');
        setTemplatePreviewItems([]);
        setTemplatePreviewError('');
        setShowAddTemplateModal(true);
    };

    const closeAddTemplateModal = () => {
        setShowAddTemplateModal(false);
        setSelectedTemplateId('');
        setTemplatePreviewItems([]);
        setTemplatePreviewError('');
    };

    const handleSubmitAddFromTemplate = async (e) => {
        e.preventDefault();
        const templateId = Number(selectedTemplateId);
        const createdById = Number(currentUser?.userId);
        if (!templateId || Number.isNaN(templateId)) {
            alert('Please select a menu template.');
            return;
        }
        if (!menuDate.trim()) {
            alert('Please select a menu date.');
            return;
        }
        if (menuDate.trim() < minMenuDate) {
            alert('Menu date cannot be in the past.');
            return;
        }
        if (!createdById || Number.isNaN(createdById)) {
            alert('Could not determine user id. Please sign in again.');
            return;
        }
        setAddingFromTemplate(true);
        try {
            const data = await addDailyMenuFromTemplate({
                templateId,
                menuDate: menuDate.trim(),
                createdById,
            });
            const addedDate = menuDate.trim();
            recordDailyMenuTemplateDate(templateId, addedDate);
            alert(
                data.message ||
                    `Saved ${data.itemsAdded ?? 0} daily menu item(s) from ${menuDateWeekdayLabel}'s template.`
            );
            closeAddTemplateModal();
            if (typeof onViewMenuDateChange === 'function') {
                await onViewMenuDateChange(addedDate);
            } else if (typeof onRefreshDailyMenu === 'function') {
                await onRefreshDailyMenu(addedDate);
            }
        } catch (err) {
            console.error('Add daily menu from template:', err?.response?.data || err);
            alert(
                err?.response?.data?.message ||
                    err?.response?.data?.error ||
                    'Failed to add daily menu from template.'
            );
        } finally {
            setAddingFromTemplate(false);
        }
    };

    const cleanImageUrl = (value) => {
        if (value == null) return '';
        const url = String(value).trim().replace(/%22|"$/g, '');
        return url;
    };

    const getMenuItemImageUrl = (menuItem) => {
        if (!menuItem || typeof menuItem !== 'object') return '';
        return cleanImageUrl(
            menuItem.imageUrl ??
            menuItem.image_url ??
            menuItem.Image_Url ??
            menuItem.ImageUrl ??
            ''
        );
    };

    const resolveDailyMenuImageUrl = (dailyItem) => {
        const directUrl = cleanImageUrl(
            dailyItem?.imageUrl ??
            dailyItem?.image_url ??
            dailyItem?.Image_Url ??
            dailyItem?.ImageUrl ??
            ''
        );
        if (directUrl) return directUrl;

        const allMenuItems = Object.values(catalogMenuItems || {}).flat();
        const matchedById = allMenuItems.find((menuItem) =>
            String(menuItem?.Food_Item_Id ?? '') === String(dailyItem?.Food_Item_Id ?? '')
        );
        const idMatchUrl = getMenuItemImageUrl(matchedById);
        if (idMatchUrl) return idMatchUrl;

        const matchedByName = allMenuItems.find((menuItem) =>
            String(menuItem?.Food_Item ?? '').trim().toLowerCase() ===
            String(dailyItem?.Food_Item ?? '').trim().toLowerCase()
        );
        return getMenuItemImageUrl(matchedByName);
    };

    const resolveDailyMenuIngredients = (dailyItem) => {
        const directIngredients = formatOnlineIngredientsDisplay(dailyItem);
        if (directIngredients) return directIngredients;

        const allMenuItems = Object.values(catalogMenuItems || {}).flat();
        const matchedById = allMenuItems.find((menuItem) =>
            String(menuItem?.Food_Item_Id ?? '') === String(dailyItem?.Food_Item_Id ?? '')
        );
        const idMatchIngredients = formatOnlineIngredientsDisplay(matchedById);
        if (idMatchIngredients) return idMatchIngredients;

        const matchedByName = allMenuItems.find((menuItem) =>
            String(menuItem?.Food_Item ?? '').trim().toLowerCase() ===
            String(dailyItem?.Food_Item ?? '').trim().toLowerCase()
        );
        return formatOnlineIngredientsDisplay(matchedByName);
    };

    const ingredientsListFromSource = (source) => {
        if (!source || !Array.isArray(source.ingredients) || source.ingredients.length === 0) {
            return [];
        }
        return source.ingredients
            .map((ing) => ing.ingredientName || ing.ingredient_name || ing.name || '')
            .filter(Boolean);
    };

    const resolveDailyMenuIngredientsList = (dailyItem) => {
        let list = ingredientsListFromSource(dailyItem);
        if (list.length > 0) return list;

        const allMenuItems = Object.values(catalogMenuItems || {}).flat();
        const matchedById = allMenuItems.find((menuItem) =>
            String(menuItem?.Food_Item_Id ?? '') === String(dailyItem?.Food_Item_Id ?? '')
        );
        list = ingredientsListFromSource(matchedById);
        if (list.length > 0) return list;

        const matchedByName = allMenuItems.find((menuItem) =>
            String(menuItem?.Food_Item ?? '').trim().toLowerCase() ===
            String(dailyItem?.Food_Item ?? '').trim().toLowerCase()
        );
        list = ingredientsListFromSource(matchedByName);
        if (list.length > 0) return list;

        const display = resolveDailyMenuIngredients(dailyItem);
        if (!display) return [];
        return parseIngredientsDisplayList(display);
    };

    const openIngredientsPopup = (item) => {
        const ingredients = resolveDailyMenuIngredientsList(item);
        setIngredientsPopup({
            foodItemName: item.Food_Item ?? 'Item',
            ingredients,
        });
    };

    const closeIngredientsPopup = () => setIngredientsPopup(null);

    const resolveDailyMenuItemFoodType = (dailyItem) => {
        const t =
            dailyItem?.itemFoodType ??
            dailyItem?.Item_Food_Type ??
            dailyItem?.foodType ??
            '';
        return t && String(t).trim() ? String(t).trim() : '—';
    };

    const resolveDailyMenuExtraInfo = (dailyItem) => {
        const ing = resolveDailyMenuIngredients(dailyItem);
        if (ing) return ing;
        const desc = dailyItem?.description ?? dailyItem?.Description;
        if (desc != null && String(desc).trim() !== '') return String(desc).trim();
        const origin = dailyItem?.cuisineOrigin ?? dailyItem?.CuisineOrigin;
        if (origin != null && String(origin).trim() !== '') return String(origin).trim();
        return '—';
    };

    const dailyMenuExistingFoodIds = useMemo(
        () => buildDailyMenuExistingFoodIds(visibleDailyMenuItems),
        [visibleDailyMenuItems]
    );

    const addItemCatalogRows = useMemo(() => {
        const rows = [];
        Object.entries(catalogMenuItems || {}).forEach(([foodType, list]) => {
            (Array.isArray(list) ? list : []).forEach((item) => {
                if (!isMenuItemActiveStatus(item.Status ?? item.status)) return;
                if (
                    dailyMenuSourceFilter !== 'JREATS' &&
                    !foodItemMatchesTemplateSourceCatalog(item, dailyMenuSourceFilter)
                ) {
                    return;
                }
                if (menuItemIsInDailyMenu(dailyMenuExistingFoodIds, item)) {
                    return;
                }
                rows.push({ foodType, item });
            });
        });
        return rows;
    }, [catalogMenuItems, dailyMenuExistingFoodIds, dailyMenuSourceFilter]);

    const filteredAddItemCatalogRows = useMemo(() => {
        if (!addItemSearchQuery) return addItemCatalogRows;
        return addItemCatalogRows.filter(({ item }) =>
            [
                item.Food_Type,
                item.Food_Item,
                item.Price,
                item.Quantity,
                item.Notes,
                item.Meal,
                item.Menu_Source,
                item.menuSource,
                item.No_of_items_available,
                resolveDailyMenuIngredients(item),
            ].some((v) => String(v ?? '').toLowerCase().includes(addItemSearchQuery))
        );
    }, [addItemCatalogRows, addItemSearchQuery]);

    const sortedAddItemCatalogRows = useMemo(() => {
        if (addItemSort?.key) {
            return sortList(filteredAddItemCatalogRows, addItemSort, getAddItemCatalogSortValue);
        }
        return [...filteredAddItemCatalogRows].sort(
            (a, b) => (a.item.Food_Item_Id || 0) - (b.item.Food_Item_Id || 0)
        );
    }, [filteredAddItemCatalogRows, addItemSort]);

    const { handleSortAsc: handleAddItemSortAsc, handleSortDesc: handleAddItemSortDesc } =
        buildSortHandlers(setAddItemSort, () => setAddItemPage(1));

    const addItemTotalPages = Math.max(
        1,
        Math.ceil(sortedAddItemCatalogRows.length / addItemPageSize)
    );
    const addItemCurrentPage = Math.min(addItemPage, addItemTotalPages);

    const paginatedAddItemCatalogRows = useMemo(() => {
        const start = (addItemCurrentPage - 1) * addItemPageSize;
        return sortedAddItemCatalogRows.slice(start, start + addItemPageSize);
    }, [sortedAddItemCatalogRows, addItemCurrentPage, addItemPageSize]);

    useEffect(() => {
        if (!showMenuTable) {
            setAddItemSearch('');
            setAddItemPage(1);
            setAddItemSort(null);
            setSelectedFoodIds(new Set());
            setPickerRowDrafts({});
            return;
        }
        setSelectedFoodIds(new Set());
        setPickerRowDrafts((prev) => {
            const next = {};
            addItemCatalogRows.forEach(({ item }) => {
                const id = String(item.Food_Item_Id ?? '');
                if (id) {
                    next[id] = prev[id] ?? defaultDailyMenuPickerDraft(item);
                }
            });
            return next;
        });
    }, [showMenuTable]);

    useEffect(() => {
        if (!showMenuTable) return;
        setPickerRowDrafts((prev) => {
            const next = { ...prev };
            let changed = false;
            addItemCatalogRows.forEach(({ item }) => {
                const id = String(item.Food_Item_Id ?? '');
                if (id && !next[id]) {
                    next[id] = defaultDailyMenuPickerDraft(item);
                    changed = true;
                }
            });
            return changed ? next : prev;
        });
    }, [showMenuTable, addItemCatalogRows]);

    useEffect(() => {
        if (!showMenuTable || selectedFoodIds.size === 0) return;
        const validIds = new Set(
            addItemCatalogRows.map(({ item }) => String(item.Food_Item_Id ?? ''))
        );
        setSelectedFoodIds((prev) => {
            const next = new Set([...prev].filter((id) => validIds.has(id)));
            return next.size === prev.size ? prev : next;
        });
    }, [showMenuTable, addItemCatalogRows, selectedFoodIds.size]);

    const selectedCount = selectedFoodIds.size;

    const allPickerSelected =
        sortedAddItemCatalogRows.length > 0 &&
        sortedAddItemCatalogRows.every(({ item }) =>
            selectedFoodIds.has(String(item.Food_Item_Id))
        );

    const toggleFoodSelection = (foodItemId) => {
        const key = String(foodItemId);
        setSelectedFoodIds((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const toggleSelectAllPicker = () => {
        if (allPickerSelected) {
            setSelectedFoodIds(new Set());
            return;
        }
        setSelectedFoodIds(
            new Set(sortedAddItemCatalogRows.map(({ item }) => String(item.Food_Item_Id)))
        );
    };

    const updatePickerRowDraft = (foodId, patch) => {
        setPickerRowDrafts((prev) => ({
            ...prev,
            [foodId]: {
                ...getDailyMenuPickerRowDraft(prev, foodId),
                ...patch,
            },
        }));
    };

    const handleBulkAddSelected = async () => {
        if (selectedCount === 0) {
            alert('Select at least one menu item.');
            return;
        }
        if (typeof handleBulkAddDailyMenuItems !== 'function') return;

        const payloads = [];
        for (const foodId of selectedFoodIds) {
            const row = addItemCatalogRows.find(
                ({ item }) => String(item.Food_Item_Id) === foodId
            );
            if (!row) continue;
            const { item } = row;
            const draft = getDailyMenuPickerRowDraft(pickerRowDrafts, foodId, item);
            if (parseMealsCsv(draft.meal).length === 0) {
                alert(`Select at least one meal type for "${item.Food_Item}".`);
                return;
            }
            const availability = String(draft.noOfItemsAvailable ?? '').trim();
            if (availability === '' || Number.isNaN(Number(availability)) || Number(availability) < 0) {
                alert(`Enter availability for "${item.Food_Item}".`);
                return;
            }
            if (!String(draft.price ?? '').trim()) {
                alert(`Enter price for "${item.Food_Item}".`);
                return;
            }
            payloads.push({
                foodItemId: Number(foodId),
                foodType: item.Food_Type ?? item.foodType ?? '',
                price: draft.price,
                meal: draft.meal,
                no_of_items_available: Number(availability),
                notes: item.Notes ?? item.notes ?? '',
            });
        }

        if (payloads.length === 0) {
            alert('No valid items selected.');
            return;
        }

        setAddingBulkItems(true);
        try {
            const result = await handleBulkAddDailyMenuItems(payloads, {
                menuDate: viewMenuDate,
                menuSource: dailyMenuSourceFilter,
            });
            if (!result) return;
            if (result.added > 0 && result.failed === 0) {
                alert(
                    result.added === 1
                        ? 'Daily menu item added successfully!'
                        : `${result.added} daily menu items added successfully!`
                );
                dismissAddItemModal();
            } else if (result.added > 0) {
                alert(
                    `Added ${result.added} item(s). ${result.failed} item(s) failed.${
                        result.lastError ? ` ${result.lastError}` : ''
                    }`
                );
                setSelectedFoodIds(new Set());
            } else {
                alert(
                    `Failed to add daily menu items.${
                        result.lastError ? ` ${result.lastError}` : ''
                    }`
                );
            }
        } finally {
            setAddingBulkItems(false);
        }
    };

    const dailySearchQuery = dailyMenuSearch.trim().toLowerCase();

    const filteredDailyMenuItems = useMemo(() => {
        const arr = visibleDailyMenuItems;
        if (!dailySearchQuery) return arr;
        return arr.filter((item) =>
            [
                item.Food_Category,
                item.foodCategory,
                item.Food_Type,
                item.itemFoodType,
                item.Item_Food_Type,
                item.Food_Item,
                item.Meal,
                item.Meal_Type,
                item.Price,
                item.Quantity,
                item.Notes,
                item.description,
                item.cuisineOrigin,
                item.No_of_items_available,
                item.Food_Item_Id,
                item.Daily_Item_Id,
                item.menuDate,
                item.templateId,
                resolveDailyMenuExtraInfo(item),
            ].some((v) => String(v ?? '').toLowerCase().includes(dailySearchQuery))
        );
    }, [visibleDailyMenuItems, dailySearchQuery]);

    const getDailyMenuSortValue = useCallback(
        (item, sortKey) => {
            switch (sortKey) {
                case 'category':
                    return String(item.Food_Category ?? item.Food_Type ?? '').trim();
                case 'foodItem':
                    return String(item.Food_Item ?? '').trim();
                case 'meal':
                    return String(item.Meal ?? item.Meal_Type ?? '').trim();
                case 'price':
                    return Number.parseFloat(item.Price) || 0;
                case 'quantity': {
                    const n = Number.parseFloat(item.Quantity);
                    return Number.isFinite(n) ? n : String(item.Quantity ?? '').trim();
                }
                case 'foodType': {
                    const t = resolveDailyMenuItemFoodType(item);
                    return t === '—' ? '' : String(t).trim();
                }
                case 'availability': {
                    const n = Number.parseFloat(item.No_of_items_available);
                    return Number.isFinite(n) ? n : String(item.No_of_items_available ?? '').trim();
                }
                case 'ingredients':
                    return resolveDailyMenuIngredients(item).trim();
                default:
                    return '';
            }
        },
        []
    );

    const sortedFilteredDailyMenuItems = useMemo(() => {
        if (!dailyMenuSort?.key) return filteredDailyMenuItems;
        return [...filteredDailyMenuItems].sort((a, b) =>
            compareDailyMenuRows(
                a,
                b,
                dailyMenuSort.key,
                dailyMenuSort.direction,
                getDailyMenuSortValue
            )
        );
    }, [filteredDailyMenuItems, dailyMenuSort, getDailyMenuSortValue]);

    const handleDailyMenuSortAsc = (key) => {
        if (typeof onPageChange === 'function') {
            onPageChange(1);
        }
        setDailyMenuSort((prev) => {
            if (prev?.key === key && prev.direction === 'asc') {
                return null;
            }
            return { key, direction: 'asc' };
        });
    };

    const handleDailyMenuSortDesc = (key) => {
        if (typeof onPageChange === 'function') {
            onPageChange(1);
        }
        setDailyMenuSort((prev) => {
            if (prev?.key === key && prev.direction === 'desc') {
                return null;
            }
            return { key, direction: 'desc' };
        });
    };

    const hasSourceDailyMenuRows = visibleDailyMenuItems.length > 0;

    useEffect(() => {
        if (dailySearchSkipInitialPageReset.current) {
            dailySearchSkipInitialPageReset.current = false;
            return;
        }
        if (typeof onPageChange === 'function') {
            onPageChange(1);
        }
    }, [dailySearchQuery]); // eslint-disable-line react-hooks/exhaustive-deps -- reset page when query changes only

    // Pagination applies to sorted, filtered daily menu rows
    const dailyMenuTotalPages = Math.max(
        1,
        Math.ceil(sortedFilteredDailyMenuItems.length / dailyMenuPageSize)
    );
    const dailyMenuPageSafe = Math.min(Math.max(1, currentPage), dailyMenuTotalPages);
    const indexOfFirstItem = (dailyMenuPageSafe - 1) * dailyMenuPageSize;
    const indexOfLastItem = indexOfFirstItem + dailyMenuPageSize;
    const currentDailyMenuItems = sortedFilteredDailyMenuItems.slice(
        indexOfFirstItem,
        indexOfLastItem
    );

    const handleDailyMenuPageSizeChange = (nextSize) => {
        setDailyMenuPageSize(nextSize);
        if (typeof onPageChange === 'function') {
            onPageChange(1);
        }
    };

    const handleAddItemPageSizeChange = (nextSize) => {
        setAddItemPageSize(nextSize);
        setAddItemPage(1);
    };

    useEffect(() => {
        if (currentPage > dailyMenuTotalPages && typeof onPageChange === 'function') {
            onPageChange(dailyMenuTotalPages);
        }
    }, [currentPage, dailyMenuTotalPages, onPageChange]);

    return (
        <div className="daily-menu-content daily-menu">
            <div className="daily-menu-header-container">
                <div className="daily-menu-header-actions">
                    <label htmlFor="daily-menu-view-date" className="daily-menu-toolbar-label">
                        Menu date
                    </label>
                    <input
                        id="daily-menu-view-date"
                        type="date"
                        className="daily-menu-toolbar-input daily-menu-toolbar-date"
                        value={viewMenuDate}
                        onChange={(e) => {
                            const v = e.target.value;
                            if (!v || typeof onViewMenuDateChange !== 'function') return;
                            onViewMenuDateChange(v);
                        }}
                        disabled={!restaurantId}
                    />
                    <div className="daily-menu-header-actions-btns">
                        {dailyMenuSourceFilter === 'JREATS' ? (
                            <JreatsStatewideMenuBrowser restaurantId={restaurantId} />
                        ) : (
                            <button
                                type="button"
                                className="add-button"
                                onClick={handleAddDailyMenu}
                                disabled={!restaurantId || isPastMenuDate}
                                title={
                                    isPastMenuDate
                                        ? 'Cannot add items for past dates'
                                        : 'Add items from the restaurant menu'
                                }
                            >
                                Add Item
                            </button>
                        )}
                        <button
                            type="button"
                            className="daily-menu-toolbar-btn"
                            onClick={openAddTemplateModal}
                            disabled={!restaurantId || hasSourceDailyMenuRows || isPastMenuDate}
                            title={
                                isPastMenuDate
                                    ? 'Cannot add daily menu for past dates'
                                    : hasSourceDailyMenuRows
                                      ? `${dailyMenuSourceFilter} daily menu already exists for the selected date`
                                      : dailyMenuSourceFilter === 'JREATS'
                                        ? 'Publish today’s Jr Eats menu from a template'
                                        : `Add ${dailyMenuSourceFilter} daily menu items from a template`
                            }
                        >
                            {dailyMenuSourceFilter === 'JREATS'
                                ? 'Publish from template'
                                : 'Add Daily Menu'}
                        </button>
                    </div>
                </div>
                {dailyMenuSourceFilter === 'JREATS' ? (
                    <p className="menu-template-jreats-state-hint menu-toolbar-jreats-hint">
                        Jr Eats dishes come from the statewide catalog. Create a{' '}
                        <strong>Template</strong>, then publish it here for the selected date.
                    </p>
                ) : null}
            </div>
            {menuSourceLoadError ? (
                <p className="menu-template-items-load-error menu-toolbar-menu-error">
                    {menuSourceLoadError}
                </p>
            ) : null}

            {ingredientsPopup && (
                <div
                    className="inventory-modal-overlay"
                    role="presentation"
                    onClick={closeIngredientsPopup}
                >
                    <div
                        className="inventory-modal daily-menu-ingredients-modal"
                        role="dialog"
                        aria-labelledby="daily-menu-ingredients-popup-title"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="inventory-modal-header">
                            <h4 id="daily-menu-ingredients-popup-title">
                                Ingredients — {formatFoodItemDisplayName(ingredientsPopup.foodItemName)}
                            </h4>
                            <button
                                type="button"
                                className="menu-template-modal-close"
                                aria-label="Close"
                                onClick={closeIngredientsPopup}
                            >
                                <FontAwesomeIcon icon={faXmark} />
                            </button>
                        </div>
                        {ingredientsPopup.ingredients.length > 0 ? (
                            <ul className="daily-menu-ingredients-popup-list">
                                {ingredientsPopup.ingredients.map((name, index) => (
                                    <li key={`${name}-${index}`}>{name}</li>
                                ))}
                            </ul>
                        ) : (
                            <p className="daily-menu-ingredients-popup-empty menu-empty-filtered">
                                No ingredients listed.
                            </p>
                        )}
                    </div>
                </div>
            )}

            {showAddTemplateModal && (
                <div
                    className="inventory-modal-overlay"
                    role="presentation"
                    onClick={closeAddTemplateModal}
                >
                    <div
                        className="inventory-modal daily-menu-template-publish-modal"
                        role="dialog"
                        aria-labelledby="add-daily-menu-template-title"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="inventory-modal-header">
                            <h4 id="add-daily-menu-template-title">
                                Publish {menuDateWeekdayLabel} default template
                            </h4>
                            <button
                                type="button"
                                className="menu-template-modal-close"
                                aria-label="Close"
                                onClick={closeAddTemplateModal}
                            >
                                <FontAwesomeIcon icon={faXmark} />
                            </button>
                        </div>
                        <form
                            className="daily-menu-add-template-form"
                            onSubmit={handleSubmitAddFromTemplate}
                        >
                            <div className="inventory-form-group">
                                <label htmlFor="daily-menu-date">Menu date</label>
                                <input
                                    id="daily-menu-date"
                                    type="date"
                                    value={menuDate}
                                    min={minMenuDate}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        setMenuDate(v && v < minMenuDate ? minMenuDate : v);
                                    }}
                                    required
                                    disabled={addingFromTemplate}
                                />
                                <p className="daily-menu-template-day-hint">
                                    Day: <strong>{menuDateWeekdayLabel}</strong>
                                    {selectedTemplate
                                        ? ` · using “${selectedTemplate.templateName || 'Template'}”`
                                        : null}
                                </p>
                            </div>
                            <div className="inventory-form-group">
                                <label htmlFor="daily-menu-template-select">
                                    Template (auto from day)
                                </label>
                                {templatesLoading ? (
                                    <p className="menu-empty-filtered">Loading templates…</p>
                                ) : (
                                    <SearchableSelect
                                        id="daily-menu-template-select"
                                        value={selectedTemplateId}
                                        onChange={setSelectedTemplateId}
                                        options={templateSelectOptions}
                                        placeholder="— No default template for this day —"
                                        searchPlaceholder="Search templates"
                                        disabled={addingFromTemplate || menuTemplates.length === 0}
                                        emptyMessage="No templates found"
                                    />
                                )}
                                {!templatesLoading && menuTemplates.length === 0 && (
                                    <p className="menu-empty-filtered">
                                        No active templates for this restaurant. Create one under My
                                        templates.
                                    </p>
                                )}
                                {!templatesLoading &&
                                    menuTemplates.length > 0 &&
                                    !selectedTemplateId && (
                                        <p className="menu-empty-filtered">
                                            No default template for {menuDateWeekdayLabel}. Mark one
                                            as Default for this day, or pick a template above.
                                        </p>
                                    )}
                            </div>

                            <div className="inventory-form-group daily-menu-template-preview">
                                <label>Items to publish</label>
                                {templatePreviewLoading ? (
                                    <p className="menu-empty-filtered">Loading items…</p>
                                ) : null}
                                {!templatePreviewLoading && templatePreviewError ? (
                                    <p className="menu-template-items-load-error">
                                        {templatePreviewError}
                                    </p>
                                ) : null}
                                {!templatePreviewLoading &&
                                !templatePreviewError &&
                                selectedTemplateId &&
                                templatePreviewItems.length === 0 ? (
                                    <p className="menu-empty-filtered">
                                        This template has no available items yet.
                                    </p>
                                ) : null}
                                {!templatePreviewLoading && templatePreviewItems.length > 0 ? (
                                    <div className="daily-menu-template-preview-table-wrap">
                                        <table className="menu-table daily-menu-template-preview-table">
                                            <thead>
                                                <tr>
                                                    <th>Food Item</th>
                                                    <th>Meal</th>
                                                    <th>Price ₹</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {templatePreviewItems.map((item, index) => (
                                                    <tr
                                                        key={
                                                            item.tmpItemId ??
                                                            item.foodItemId ??
                                                            `${item.foodItemName}-${index}`
                                                        }
                                                    >
                                                        <td>
                                                            {formatFoodItemDisplayName(
                                                                item.foodItemName ??
                                                                    item.Food_Item ??
                                                                    '—'
                                                            )}
                                                        </td>
                                                        <td>{item.meal || '—'}</td>
                                                        <td>{item.price ?? '—'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : null}
                            </div>

                            <div className="inventory-form-actions">
                                <button
                                    type="submit"
                                    className="add-button"
                                    disabled={
                                        addingFromTemplate ||
                                        templatesLoading ||
                                        !selectedTemplateId ||
                                        templatePreviewLoading
                                    }
                                >
                                    {addingFromTemplate ? 'Saving…' : 'Save daily menu'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <OrdersTablePanel
                searchValue={dailyMenuSearch}
                onSearchChange={setDailyMenuSearch}
                resultCount={sortedFilteredDailyMenuItems.length}
                resultLabel="item"
                searchAriaLabel="Search daily menu items"
            >
            <div className="daily-menu-table-wrapper">
                <table className="menu-table">
                    <thead>
                        <tr>
                            <OrdersSortableTh
                                label="Food Item"
                                sortKey="foodItem"
                                activeSortKey={dailyMenuSort?.key}
                                sortDirection={dailyMenuSort?.direction}
                                onSortAsc={handleDailyMenuSortAsc}
                                onSortDesc={handleDailyMenuSortDesc}
                            />
                            <OrdersSortableTh
                                label="Food Category"
                                sortKey="category"
                                activeSortKey={dailyMenuSort?.key}
                                sortDirection={dailyMenuSort?.direction}
                                onSortAsc={handleDailyMenuSortAsc}
                                onSortDesc={handleDailyMenuSortDesc}
                            />
                            <OrdersSortableTh
                                label="Meal"
                                sortKey="meal"
                                activeSortKey={dailyMenuSort?.key}
                                sortDirection={dailyMenuSort?.direction}
                                onSortAsc={handleDailyMenuSortAsc}
                                onSortDesc={handleDailyMenuSortDesc}
                            />
                            <OrdersSortableTh
                                label={
                                    dailyMenuSourceFilter === 'JREATS'
                                        ? 'Price & Quantity'
                                        : 'Price ₹'
                                }
                                sortKey="price"
                                activeSortKey={dailyMenuSort?.key}
                                sortDirection={dailyMenuSort?.direction}
                                onSortAsc={handleDailyMenuSortAsc}
                                onSortDesc={handleDailyMenuSortDesc}
                            />
                            {dailyMenuSourceFilter === 'JREATS' ? null : (
                                <OrdersSortableTh
                                    label="Quantity"
                                    sortKey="quantity"
                                    activeSortKey={dailyMenuSort?.key}
                                    sortDirection={dailyMenuSort?.direction}
                                    onSortAsc={handleDailyMenuSortAsc}
                                    onSortDesc={handleDailyMenuSortDesc}
                                />
                            )}
                            <OrdersSortableTh
                                label="Food Type"
                                sortKey="foodType"
                                activeSortKey={dailyMenuSort?.key}
                                sortDirection={dailyMenuSort?.direction}
                                onSortAsc={handleDailyMenuSortAsc}
                                onSortDesc={handleDailyMenuSortDesc}
                            />
                            <OrdersSortableTh
                                label="Availability"
                                sortKey="availability"
                                activeSortKey={dailyMenuSort?.key}
                                sortDirection={dailyMenuSort?.direction}
                                onSortAsc={handleDailyMenuSortAsc}
                                onSortDesc={handleDailyMenuSortDesc}
                            />
                            <OrdersSortableTh
                                label="Ingredients"
                                sortKey="ingredients"
                                activeSortKey={dailyMenuSort?.key}
                                sortDirection={dailyMenuSort?.direction}
                                onSortAsc={handleDailyMenuSortAsc}
                                onSortDesc={handleDailyMenuSortDesc}
                            />
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentDailyMenuItems.length > 0 ? (
                        currentDailyMenuItems.map((item) => {
                            const rowId = resolveDailyMenuRowId(item);
                            return (
                            <tr
                                key={rowId ?? `dm-${item.Food_Item_Id}`}
                            >
                                <td className="menu-template-picker-food-cell">
                                    <div className="menu-food-cell-inner">
                                    <img
                                        className="menu-template-item-thumb menu-table-thumb"
                                        src={resolveDailyMenuImageUrl(item) || defaultFoodImage}
                                        alt=""
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = defaultFoodImage;
                                        }}
                                    />
                                    <span className="menu-table-food-name">{formatFoodItemDisplayName(item.Food_Item)}</span>
                                    </div>
                                </td>
                                <td>
                                    <span className="menu-table-cell-muted">
                                        {item.Food_Category ?? item.Food_Type ?? '—'}
                                    </span>
                                </td>
                                <td>{item.Meal}</td>
                                <td>
                                    {dailyMenuSourceFilter === 'JREATS' ? (
                                        <AgePricingComboCell
                                            item={item}
                                            fallbackPrice={item.Price}
                                            fallbackQuantity={resolveDailyMenuQuantity(item)}
                                        />
                                    ) : (
                                        item.Price
                                    )}
                                </td>
                                {dailyMenuSourceFilter === 'JREATS' ? null : (
                                    <td>{item.Quantity}</td>
                                )}
                                <td>{resolveDailyMenuItemFoodType(item)}</td>
                                <td>
                                    {formatDailyMenuAvailability(item.No_of_items_available)}
                                </td>
                                <td className="menu-table-cell-ingredients">
                                    {(() => {
                                        const ingredientsList = resolveDailyMenuIngredientsList(item);
                                        const ingredientsDisplay =
                                            ingredientsList.join(', ') ||
                                            resolveDailyMenuIngredients(item);
                                        if (!ingredientsDisplay) {
                                            return '—';
                                        }
                                        const ingredientsPreview = formatIngredientsTablePreview(
                                            ingredientsList,
                                            ingredientsDisplay
                                        );
                                        return (
                                            <div className="daily-menu-ingredients-preview-wrap">
                                                <span
                                                    className="daily-menu-ingredients-inline"
                                                    title={ingredientsDisplay}
                                                >
                                                    {ingredientsPreview}
                                                </span>
                                                <button
                                                    type="button"
                                                    className="daily-menu-ingredients-info-btn"
                                                    aria-label={`View ingredients for ${item.Food_Item}`}
                                                    title="View ingredients"
                                                    onClick={() => openIngredientsPopup(item)}
                                                >
                                                    <FontAwesomeIcon icon={faCircleInfo} />
                                                </button>
                                            </div>
                                        );
                                    })()}
                                </td>
                                <td className="menu-table-actions-cell">
                                    {isPastMenuDate ? (
                                        '—'
                                    ) : (
                                        <>
                                            <button className="edit-button" onClick={() => handleEditDailyMenuItem(rowId)}>
                                                <FontAwesomeIcon icon={faEdit} />
                                            </button>
                                            <button className="delete-button" onClick={() => handleDeleteDailyMenuItem(rowId)}>
                                                <FontAwesomeIcon icon={faTrash} />
                                            </button>
                                        </>
                                    )}
                                </td>
                            </tr>
                            );
                        })
                        ) : (
                            <tr>
                                <td
                                    colSpan={dailyMenuSourceFilter === 'JREATS' ? 8 : 9}
                                    className="menu-table-empty-cell menu-empty-filtered"
                                >
                                    {dailySearchQuery
                                        ? 'No daily menu items match your search.'
                                        : `No ${dailyMenuSourceFilter} daily menu items available.`}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            </OrdersTablePanel>

            {editingItemId ? (
                <div
                    className="inventory-modal-overlay"
                    role="presentation"
                    onClick={handleCloseEdit}
                >
                    <div
                        className="inventory-modal menu-template-modal daily-menu-edit-modal"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="daily-menu-edit-title"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="inventory-modal-header menu-template-modal-header">
                            <h4 id="daily-menu-edit-title">Edit daily menu item</h4>
                            <button
                                type="button"
                                className="menu-template-modal-close"
                                onClick={handleCloseEdit}
                                aria-label="Close"
                            >
                                <FontAwesomeIcon icon={faXmark} />
                            </button>
                        </div>
                        <form
                            className="inventory-form inventory-form--supplier-modal menu-template-edit-form daily-menu-edit-form"
                            onSubmit={(e) => {
                                e.preventDefault();
                                handleUpdateDailyMenuItem(editingItemId);
                            }}
                        >
                            <div className="daily-menu-edit-meta-row">
                                <div className="inventory-form-group">
                                    <label htmlFor="daily-menu-edit-food-item">Food item</label>
                                    <input
                                        id="daily-menu-edit-food-item"
                                        type="text"
                                        className="add-menu-form-field inventory-readonly-field"
                                        value={tempDailyMenuItem.Food_Item ?? ''}
                                        readOnly={menuTypeFilter === 'online'}
                                        disabled={menuTypeFilter === 'online'}
                                        onChange={(e) =>
                                            setTempDailyMenuItem({
                                                ...tempDailyMenuItem,
                                                Food_Item: e.target.value,
                                            })
                                        }
                                    />
                                </div>
                                <div className="inventory-form-group">
                                    <label htmlFor="daily-menu-edit-category">Food category</label>
                                    <input
                                        id="daily-menu-edit-category"
                                        type="text"
                                        className="add-menu-form-field inventory-readonly-field"
                                        value={tempDailyMenuItem.Food_Type ?? ''}
                                        readOnly={menuTypeFilter === 'online'}
                                        disabled={menuTypeFilter === 'online'}
                                        onChange={(e) =>
                                            setTempDailyMenuItem({
                                                ...tempDailyMenuItem,
                                                Food_Type: e.target.value,
                                            })
                                        }
                                    />
                                </div>
                            </div>
                            <div className="menu-template-edit-meal-avail-row">
                                <div className="inventory-form-group menu-template-edit-meal-group daily-menu">
                                    <label className="menu-form-label--required">Meal types</label>
                                    <MealTypesDropdown
                                        id={`daily-menu-edit-meal-${editingItemId}`}
                                        value={tempDailyMenuItem.Meal ?? ''}
                                        onCsvChange={(csv) =>
                                            setTempDailyMenuItem({
                                                ...tempDailyMenuItem,
                                                Meal: csv,
                                            })
                                        }
                                    />
                                </div>
                                <div className="inventory-form-group menu-template-edit-avail-group">
                                    <label htmlFor="daily-menu-edit-stock">Availability</label>
                                    <input
                                        id="daily-menu-edit-stock"
                                        type="number"
                                        className="add-menu-form-field"
                                        value={tempDailyMenuItem.No_of_items_available ?? ''}
                                        onChange={(e) =>
                                            setTempDailyMenuItem({
                                                ...tempDailyMenuItem,
                                                No_of_items_available: e.target.value,
                                            })
                                        }
                                    />
                                </div>
                            </div>
                            <div className="menu-template-edit-price-qty-row">
                                {dailyMenuSourceFilter === 'JREATS' ? (
                                    <div className="inventory-form-group menu-template-edit-full-row">
                                        <label className="menu-form-label--required">Price</label>
                                        <AgePricingComboEditor
                                            item={tempDailyMenuItem}
                                            basePrice={tempDailyMenuItem.Price}
                                            baseQuantity={resolveDailyMenuQuantity(
                                                tempDailyMenuItem
                                            )}
                                            agePricing={tempDailyMenuItem.agePricing}
                                            onChange={({ basePrice, agePricing }) =>
                                                setTempDailyMenuItem({
                                                    ...tempDailyMenuItem,
                                                    Price: basePrice,
                                                    agePricing,
                                                })
                                            }
                                        />
                                    </div>
                                ) : (
                                    <>
                                        <div className="inventory-form-group">
                                            <label
                                                htmlFor="daily-menu-edit-price"
                                                className="menu-form-label--required"
                                            >
                                                Price
                                            </label>
                                            <input
                                                id="daily-menu-edit-price"
                                                type="text"
                                                className="add-menu-form-field"
                                                value={tempDailyMenuItem.Price ?? ''}
                                                onChange={(e) =>
                                                    setTempDailyMenuItem({
                                                        ...tempDailyMenuItem,
                                                        Price: e.target.value,
                                                    })
                                                }
                                                required
                                            />
                                        </div>
                                        <div className="inventory-form-group">
                                            <label htmlFor="daily-menu-edit-qty">Quantity</label>
                                            <input
                                                id="daily-menu-edit-qty"
                                                type="text"
                                                className="add-menu-form-field"
                                                value={tempDailyMenuItem.Quantity ?? ''}
                                                onChange={(e) =>
                                                    setTempDailyMenuItem({
                                                        ...tempDailyMenuItem,
                                                        Quantity: e.target.value,
                                                    })
                                                }
                                            />
                                        </div>
                                    </>
                                )}
                            </div>
                            <div className="inventory-form-actions menu-template-modal-actions menu-template-edit-modal-actions daily-menu-edit-actions">
                                <button type="submit" className="add-button menu-template-edit-submit-btn">
                                    Update
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            ) : null}

            {sortedFilteredDailyMenuItems.length > 0 ? (
                <OrdersTablePagination
                    currentPage={dailyMenuPageSafe}
                    totalPages={dailyMenuTotalPages}
                    totalItems={sortedFilteredDailyMenuItems.length}
                    pageSize={dailyMenuPageSize}
                    onPageChange={onPageChange}
                    onPageSizeChange={handleDailyMenuPageSizeChange}
                    itemLabel="items"
                    ariaLabel="Daily menu table pagination"
                />
            ) : null}

            {showMenuTable && (
                <div
                    className="inventory-modal-overlay"
                    role="presentation"
                    onClick={dismissAddItemModal}
                >
                    <div
                        className="inventory-modal menu-template-modal menu-template-picker-modal daily-menu-add-item-modal"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="daily-menu-add-item-title"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="inventory-modal-header menu-template-modal-header">
                            <h4 id="daily-menu-add-item-title">
                                Add {dailyMenuSourceFilter} daily menu item
                            </h4>
                            <button
                                type="button"
                                className="menu-template-modal-close"
                                aria-label="Close"
                                onClick={dismissAddItemModal}
                                disabled={addingBulkItems}
                            >
                                <FontAwesomeIcon icon={faXmark} />
                            </button>
                        </div>
                        <div className="menu-template-picker-panel daily-menu-add-item-panel">
                            <div className="menu-template-picker-toolbar">
                                <input
                                    type="text"
                                    className="add-menu-form-field menu-template-picker-search"
                                    placeholder="Search menu items…"
                                    value={addItemSearch}
                                    onChange={(e) => setAddItemSearch(e.target.value)}
                                    disabled={addingBulkItems}
                                />
                                <div className="menu-template-picker-toolbar-actions">
                                    <button
                                        type="button"
                                        className="close-button menu-template-picker-toolbar-btn"
                                        onClick={toggleSelectAllPicker}
                                        disabled={
                                            addingBulkItems ||
                                            sortedAddItemCatalogRows.length === 0
                                        }
                                    >
                                        {allPickerSelected ? 'Deselect all' : 'Select all'}
                                    </button>
                                    <button
                                        type="button"
                                        className="add-button menu-template-picker-toolbar-btn"
                                        onClick={handleBulkAddSelected}
                                        disabled={addingBulkItems || selectedCount === 0}
                                    >
                                        {addingBulkItems
                                            ? 'Adding…'
                                            : `Add selected (${selectedCount})`}
                                    </button>
                                </div>
                            </div>
                            <div className="menu-table-wrapper menu-template-picker-table-wrap daily-menu-add-item-table-wrap">
                                <table className="menu-table">
                                    <thead>
                                        <tr>
                                            <th
                                                className="menu-template-picker-check-col"
                                                aria-hidden="true"
                                            />
                                            <OrdersSortableTh label="Food Item" sortKey="foodItem" activeSortKey={addItemSort?.key} sortDirection={addItemSort?.direction} onSortAsc={handleAddItemSortAsc} onSortDesc={handleAddItemSortDesc} />
                                            <OrdersSortableTh label="Food Category" sortKey="category" activeSortKey={addItemSort?.key} sortDirection={addItemSort?.direction} onSortAsc={handleAddItemSortAsc} onSortDesc={handleAddItemSortDesc} />
                                            <OrdersSortableTh
                                                label={
                                                    dailyMenuSourceFilter === 'JREATS'
                                                        ? 'Price & Quantity'
                                                        : 'Price ₹'
                                                }
                                                sortKey="price"
                                                activeSortKey={addItemSort?.key}
                                                sortDirection={addItemSort?.direction}
                                                onSortAsc={handleAddItemSortAsc}
                                                onSortDesc={handleAddItemSortDesc}
                                            />
                                            {dailyMenuSourceFilter === 'JREATS' ? null : (
                                                <OrdersSortableTh
                                                    label="Quantity"
                                                    sortKey="quantity"
                                                    activeSortKey={addItemSort?.key}
                                                    sortDirection={addItemSort?.direction}
                                                    onSortAsc={handleAddItemSortAsc}
                                                    onSortDesc={handleAddItemSortDesc}
                                                />
                                            )}
                                            <OrdersSortableTh label="Food Type" sortKey="foodType" activeSortKey={addItemSort?.key} sortDirection={addItemSort?.direction} onSortAsc={handleAddItemSortAsc} onSortDesc={handleAddItemSortDesc} />
                                            <OrdersSortableTh label="Availability" sortKey="availability" activeSortKey={addItemSort?.key} sortDirection={addItemSort?.direction} onSortAsc={handleAddItemSortAsc} onSortDesc={handleAddItemSortDesc} />
                                            <OrdersSortableTh label="Meal" sortKey="meal" activeSortKey={addItemSort?.key} sortDirection={addItemSort?.direction} onSortAsc={handleAddItemSortAsc} onSortDesc={handleAddItemSortDesc} />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredAddItemCatalogRows.length === 0 ? (
                                            <tr>
                                                <td colSpan={dailyMenuSourceFilter === 'JREATS' ? 7 : 8} className="menu-empty-filtered">
                                                    {addItemCatalogRows.length === 0
                                                        ? 'All menu items are already on this daily menu.'
                                                        : 'No menu items match your search.'}
                                                </td>
                                            </tr>
                                        ) : (
                                            paginatedAddItemCatalogRows.map(({ foodType, item }) => {
                                                const fid = String(item.Food_Item_Id);
                                                const checked = selectedFoodIds.has(fid);
                                                const draft = getDailyMenuPickerRowDraft(
                                                    pickerRowDrafts,
                                                    fid,
                                                    item
                                                );
                                                return (
                                                    <tr
                                                        key={`${foodType}-${item.Food_Item_Id}-${item.Price}`}
                                                        className={
                                                            checked
                                                                ? 'menu-template-picker-row--selected'
                                                                : ''
                                                        }
                                                    >
                                                        <td className="menu-template-picker-check-col">
                                                            <input
                                                                type="checkbox"
                                                                checked={checked}
                                                                onChange={() =>
                                                                    toggleFoodSelection(fid)
                                                                }
                                                                disabled={addingBulkItems}
                                                                aria-label={`Select ${item.Food_Item}`}
                                                            />
                                                        </td>
                                                        <td className="menu-template-picker-food-cell">
                                                            <div className="menu-food-cell-inner">
                                                            <img
                                                                className="menu-template-item-thumb menu-table-thumb"
                                                                src={
                                                                    getMenuItemImageUrl(item) ||
                                                                    defaultFoodImage
                                                                }
                                                                alt=""
                                                                onError={(e) => {
                                                                    e.target.onerror = null;
                                                                    e.target.src = defaultFoodImage;
                                                                }}
                                                            />
                                                            <span className="menu-table-food-name">
                                                                {formatFoodItemDisplayName(item.Food_Item)}
                                                            </span>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <span className="menu-table-cell-muted">
                                                                {item.Food_Type}
                                                            </span>
                                                        </td>
                                                        <td className="menu-template-picker-field-cell">
                                                            {dailyMenuSourceFilter === 'JREATS' &&
                                                            hasAgePricing(item) ? (
                                                                <AgePricingComboCell
                                                                    item={item}
                                                                    fallbackPrice={item.Price}
                                                                    fallbackQuantity={resolveDailyMenuQuantity(
                                                                        item
                                                                    )}
                                                                />
                                                            ) : (
                                                                <input
                                                                    type="text"
                                                                    className="menu-template-picker-cell-input"
                                                                    value={draft.price}
                                                                    onChange={(e) =>
                                                                        updatePickerRowDraft(fid, {
                                                                            price: e.target.value,
                                                                        })
                                                                    }
                                                                    disabled={addingBulkItems}
                                                                />
                                                            )}
                                                        </td>
                                                        {dailyMenuSourceFilter === 'JREATS' ? null : (
                                                            <td>{item.Quantity ?? '—'}</td>
                                                        )}
                                                        <td>
                                                            {item.itemFoodType ??
                                                                item.foodType ??
                                                                item.Food_Item_Type ??
                                                                '—'}
                                                        </td>
                                                        <td className="menu-template-picker-field-cell">
                                                            <input
                                                                type="number"
                                                                className="menu-template-picker-cell-input"
                                                                value={draft.noOfItemsAvailable}
                                                                onChange={(e) =>
                                                                    updatePickerRowDraft(fid, {
                                                                        noOfItemsAvailable:
                                                                            e.target.value,
                                                                    })
                                                                }
                                                                placeholder="Enter availability"
                                                                disabled={addingBulkItems}
                                                            />
                                                        </td>
                                                        <td className="menu-template-picker-field-cell menu-template-picker-meal-cell">
                                                            <div className="daily-menu menu-template-meal-dropdown">
                                                                <MealTypesDropdown
                                                                    id={`add-${foodType}-${item.Food_Item_Id}`}
                                                                    value={draft.meal}
                                                                    onCsvChange={(csv) =>
                                                                        updatePickerRowDraft(fid, {
                                                                            meal: csv,
                                                                        })
                                                                    }
                                                                />
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            {sortedAddItemCatalogRows.length > 0 ? (
                                <OrdersTablePagination
                                    currentPage={addItemCurrentPage}
                                    totalPages={addItemTotalPages}
                                    totalItems={sortedAddItemCatalogRows.length}
                                    pageSize={addItemPageSize}
                                    onPageChange={setAddItemPage}
                                    onPageSizeChange={handleAddItemPageSizeChange}
                                    itemLabel="items"
                                    ariaLabel="Add daily menu item pagination"
                                />
                            ) : null}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DailyMenu;
